import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/lib/branding";
import { api } from "@/lib/api";
import KioskApp from "@/kiosk/KioskApp";
import DemoKiosks from "@/admin/DemoKiosks";
import UptimeReport from "@/admin/UptimeReport";
import Login from "@/admin/Login";
import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import Products from "@/admin/Products";
import Media from "@/admin/Media";
import Categories from "@/admin/Categories";
import BuildingGroups from "@/admin/BuildingGroups";
import Hierarchy from "@/admin/Hierarchy";
import Stock from "@/admin/Stock";
import Planogram from "@/admin/Planogram";
import KioskPreview from "@/admin/KioskPreview";
import CategoryProducts from "@/admin/CategoryProducts";
import Users from "@/admin/Users";
import Combos from "@/admin/Combos";
import Financials from "@/admin/Financials";
import KioskTerminal from "@/admin/KioskTerminal";
import SiteStyle from "@/admin/SiteStyle";
import MetricDetail from "@/admin/MetricDetail";
import Reconciliation from "@/admin/Reconciliation";
import LoginActivity from "@/admin/LoginActivity";
import SupportTickets from "@/admin/SupportTickets";
import OfflineAlerts from "@/admin/OfflineAlerts";
import UserGuide from "@/admin/UserGuide";
import Profile from "@/admin/Profile";
import SetPassword from "@/admin/SetPassword";
import { PrivacyPolicy, Terms } from "@/legal/Legal";

function GoogleCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    if (window.location.pathname !== "/auth/google") return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) return;
    done.current = true;
    const state = params.get("state");
    const saved = sessionStorage.getItem("hv_oauth_state");
    sessionStorage.removeItem("hv_oauth_state");
    if (!state || state !== saved) {
      window.history.replaceState(null, "", "/admin/login");
      navigate("/admin/login?google_error=1", { replace: true });
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect_uri = window.location.origin + "/auth/google";
    // Retry the code exchange on transient 5xx / network errors (backend warming up right after a
    // redeploy). Only a definitive 4xx (bad/expired code, unlinked account) should show google_error.
    const exchange = async (path, body, onSuccess, onFail) => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const res = await api.post(path, body);
          onSuccess(res);
          return;
        } catch (err) {
          const status = err.response?.status;
          if (status && status >= 400 && status < 500) { onFail(); return; }
          await new Promise((r) => setTimeout(r, Math.min(800 * 2 ** attempt, 5000)));
        }
      }
      onFail();
    };
    const pending2fa = localStorage.getItem("hv_pending_2fa");
    if (pending2fa) {
      exchange(
        "/auth/2fa/google",
        { twofa_token: pending2fa, code, redirect_uri },
        (res) => {
          localStorage.removeItem("hv_pending_2fa");
          localStorage.setItem("kiosk_admin_token", res.data.token);
          if (res.data.trust_token) localStorage.setItem("hv_2fa_trust", res.data.trust_token);
          setUser(res.data.user);
          window.history.replaceState(null, "", "/admin");
          navigate("/admin", { replace: true });
        },
        () => {
          localStorage.removeItem("hv_pending_2fa");
          window.history.replaceState(null, "", "/admin/login");
          navigate("/admin/login?twofa_error=1", { replace: true });
        },
      );
      return;
    }
    exchange(
      "/auth/google/session",
      { code, redirect_uri },
      (res) => {
        localStorage.setItem("kiosk_admin_token", res.data.token);
        setUser(res.data.user);
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      },
      () => {
        window.history.replaceState(null, "", "/admin/login");
        navigate("/admin/login?google_error=1", { replace: true });
      },
    );
  }, [setUser, navigate]);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="h-screen flex items-center justify-center bg-slate-950 text-slate-300">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrandingProvider>
        <BrowserRouter>
          <GoogleCallback />
          <Routes>
            <Route path="/" element={<Navigate to="/admin" replace />} />
            <Route path="/kiosk/:code" element={<KioskApp />} />
            <Route path="/demo/:token" element={<KioskApp />} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/auth/google" element={<div className="h-screen flex items-center justify-center bg-slate-950 text-slate-300">Signing you in…</div>} />
            <Route
              path="/admin"
              element={
                <Protected>
                  <AdminLayout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="kiosk-terminal" element={<KioskTerminal />} />
              <Route path="products" element={<Products />} />
              <Route path="categories" element={<Categories />} />
              <Route path="categories/:name" element={<CategoryProducts />} />
              <Route path="media" element={<Media />} />
              <Route path="deals" element={<Combos />} />
              <Route path="hierarchy" element={<Hierarchy />} />
              <Route path="building-groups" element={<BuildingGroups />} />
              <Route path="stock" element={<Stock />} />
              <Route path="planogram" element={<Planogram />} />
              <Route path="demo-kiosks" element={<DemoKiosks />} />
              <Route path="kiosk-preview" element={<KioskPreview />} />
              <Route path="users" element={<Users />} />
              <Route path="login-activity" element={<LoginActivity />} />
              <Route path="support-tickets" element={<SupportTickets />} />
              <Route path="site-style" element={<SiteStyle />} />
              <Route path="offline-alerts" element={<OfflineAlerts />} />
              <Route path="uptime" element={<UptimeReport />} />
              <Route path="user-guide" element={<UserGuide />} />
              <Route path="insights/:metric" element={<MetricDetail />} />
              <Route path="financials" element={<Financials />} />
              <Route path="reconciliation" element={<Reconciliation />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </BrandingProvider>
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
